import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { Box } from '@mui/material';

import TableContainer from '../../components/Table/TableContainer';
import Loader from '../../components/Loader';
import { VENDORS_LIST_TABLE_LABELS } from '../../constants/tableLabels';
import usePagination from '../../hooks/usePagination';
import { ActiveSortField, FilterTypes, VendorAccountShort } from '../../types';
import NoDataText from '../../components/NoDataText';
import TableFilters from '../../components/TableFilters/TableFilters';
import TableActions from '../../components/Table/TableActions';
import { ACCOUNT_STATUS_FILTER_OPTIONS, METHOD_TYPE_FILTER_OPTIONS, VENDOR_TYPE_FILTER_OPTIONS } from '../../components/TableFilters/constants';
import useSnackBar from '../../hooks/useSnackBar';
import { useHasPermission } from '../../hooks/useHasPermission';
import { handleSortTableData } from '../../helpers/handleSortTableData';
import { getVendorAccounts, resendConfirmationEmail } from '../../api/vendorApi/vendorAccountsApi';
import VendorAccountTableRow from './VendorAccountTableRow';
import { MenuOption } from './types';

const VendorsAccountView = () => {
  const params = useParams();

  const { pageNumber, rowsPerPage, setTotalCount } = usePagination();
  const { setAlert } = useSnackBar();
  const navigate = useNavigate();
  const canCreate = useHasPermission('Vendors', 'Create');

  const [vendorsList, setvendorsList] = useState<VendorAccountShort[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [showFilters, setShowFilters] = useState<boolean>(true);

  const [filters, setFilters] = useState<FilterTypes>({
    vendorType: '',
    defaultPaymentMethod: '',
    vendorStatus: '',
  });

  const [match, setMatch] = useState<string>('');
  const [activeSortField, setActiveSortField] = useState<ActiveSortField | null>(null);
  const sortableFields = {
    name: {
      order: '',
      fieldName: 'name',
    },
    createdAt: {
      order: '',
      fieldName: 'createdAt',
    },
    updatedAt: {
      order: '',
      fieldName: 'updatedAt',
    },
  };
  const hasParams = !!Object.entries(params).length;

  useEffect(() => {
    getVendorAccounts({
      page: pageNumber,
      size: rowsPerPage,
      count: pageNumber === 1,
      match: match,
      filters: { ...filters, ...(hasParams && params) },
      activeSortField,
    }).then((res) => {
      if (res.data.resultSet) {
        pageNumber === 1 && setTotalCount?.(res.data.totalCount || 0);
        setvendorsList(res.data.resultSet);
        setIsDataLoading(false);
      }
    });
  }, [pageNumber, rowsPerPage, filters, match, activeSortField]);

  const handleEditClick = (accountId: number, option: MenuOption) => {
    if (option === MenuOption.Edit) {
      navigate(`/vendors/edit-vendor-account/${accountId}`);
    } else if (option === MenuOption.Delete) {
      console.log('Delete account with ID:', accountId);
    }
  };

  // const handleDeleteClick = (accountId: number) => {
  //   console.log('Delete account with ID:', accountId);
  // };

  const handleResendConfirmationClick = async (accountId: number) => {
    try {
      const res = await resendConfirmationEmail(accountId);
      setAlert((prev) => ({
        ...prev,
        message: res.data.message || 'Confirmation email resent successfully.',
        type: 'success',
      }));
    } catch (error) {
      setAlert((prev) => ({
        ...prev,
        message: 'Failed to send confirmation email.',
        type: 'error',
      }));
    }
  };
  return (
    <>
      {isDataLoading && !vendorsList.length ? (
        <Box position='absolute' top='50%' left='50%'>
          <Loader colorType='warning' />
        </Box>
      ) : (
        <>
          <TableActions
            hasParams={hasParams}
            setMatch={setMatch}
            match={match}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            createLink={canCreate ? '/vendors/add-vendor-account' : undefined}
            uploadLink={canCreate ? '/vendors/add-vendor-bulk-upload' : undefined}
          />

          {showFilters && (
            <TableFilters
              filters={filters}
              setFilters={setFilters}
              setMatch={setMatch}
              timeFilter={'Created'}
              match={match}
              vendorType={VENDOR_TYPE_FILTER_OPTIONS}
              defaultPaymentMethod={METHOD_TYPE_FILTER_OPTIONS}
              vendorStatus={ACCOUNT_STATUS_FILTER_OPTIONS}

            />
          )}

          {!isDataLoading && vendorsList.length === 0 ? (
            <NoDataText />
          ) : (
            <TableContainer
              headerLabels={VENDORS_LIST_TABLE_LABELS}
              sortableFields={sortableFields}
              activeSortField={activeSortField}
              setActiveSortField={setActiveSortField}
              handleSortTableData={handleSortTableData}
            >
              {/* Add your table rows/components here if needed */}
              <VendorAccountTableRow
                data={vendorsList}
                onEditButtonClick={handleEditClick}
                onRowClick={(id) => navigate(`/vendors/edit-vendor-account/${id}`)}
                // onDeleteButtonClick={handleDeleteClick}
                onResendConfirmationClick={handleResendConfirmationClick}
              />
              <>
              </>
            </TableContainer>
          )}
          <Outlet />
        </>
      )}
    </>
  );
};

export default VendorsAccountView;
